// components/Admin/DepartmentManagement.jsx
// Hospital Department Management — ported from RIS department_view.php
import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, TextField, Button, Grid, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Paper, IconButton, Tooltip,
  Dialog, DialogTitle, DialogContent, DialogActions,
  Alert, Snackbar, CircularProgress, Chip
} from '@mui/material';
import {
  Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon,
  Save as SaveIcon, Close as CloseIcon
} from '@mui/icons-material';
import { departmentService } from '../../services/ris';
import { useThemeMode } from '../../contexts/ThemeModeContext';

const emptyForm = { departmentId: '', name: '', nameEn: '' };

export default function DepartmentManagement() {
  const { mode } = useThemeMode();
  const isDark = mode === 'dark';
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const fetchDepartments = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await departmentService.list();
      setDepartments(data.departments || []);
    } catch (err) {
      console.error('Failed to load departments:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchDepartments(); }, [fetchDepartments]);

  const openCreate = () => { setEditing(null); setForm(emptyForm); setDialogOpen(true); };
  const openEdit = (dep) => { setEditing(dep); setForm({ ...dep }); setDialogOpen(true); };

  const handleSave = async () => {
    setSaving(true);
    try {
      if (editing) {
        await departmentService.update(editing._id, form);
        setSnackbar({ open: true, message: 'Department updated', severity: 'success' });
      } else {
        await departmentService.create(form);
        setSnackbar({ open: true, message: 'Department created', severity: 'success' });
      }
      setDialogOpen(false);
      fetchDepartments();
    } catch (err) {
      setSnackbar({ open: true, message: err.response?.data?.error || 'Failed to save', severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Deactivate this department?')) return;
    try {
      await departmentService.delete(id);
      setSnackbar({ open: true, message: 'Department deactivated', severity: 'success' });
      fetchDepartments();
    } catch (err) {
      setSnackbar({ open: true, message: 'Failed to deactivate', severity: 'error' });
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h5" fontWeight={700}>🏢 Department Management</Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>Add Department</Button>
      </Box>

      <TableContainer component={Paper}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 700 }}>Department ID</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Name</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>English Name</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={4} align="center" sx={{ py: 4 }}><CircularProgress size={24} /></TableCell></TableRow>
            ) : departments.length === 0 ? (
              <TableRow><TableCell colSpan={4} align="center" sx={{ py: 4 }}>
                <Typography color="text.secondary">No departments. Click "Add Department" to create one.</Typography>
              </TableCell></TableRow>
            ) : departments.map(d => (
              <TableRow key={d._id} hover>
                <TableCell><Chip label={d.departmentId} size="small" /></TableCell>
                <TableCell>{d.name}</TableCell>
                <TableCell>{d.nameEn}</TableCell>
                <TableCell>
                  <Tooltip title="Edit"><IconButton size="small" onClick={() => openEdit(d)}><EditIcon fontSize="small" /></IconButton></Tooltip>
                  <Tooltip title="Deactivate"><IconButton size="small" color="error" onClick={() => handleDelete(d._id)}><DeleteIcon fontSize="small" /></IconButton></Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        maxWidth={false}
        PaperProps={{
          sx: {
            width: { xs: '95vw', sm: '460px' },
            maxWidth: '480px !important',
            maxHeight: '85vh',
            borderRadius: '14px',
            bgcolor: isDark ? '#1E1E1E' : '#FFFFFF',
            backgroundImage: 'none',
            color: isDark ? '#F1F5F9' : '#0F172A',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden'
          }
        }}
      >
        <DialogTitle
          sx={{
            px: 2.5,
            py: 1.25,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            borderBottom: 1,
            borderColor: isDark ? 'rgba(255,255,255,0.08)' : 'divider',
            fontSize: '1.05rem',
            fontWeight: 700
          }}
        >
          {editing ? 'Edit Department' : 'New Department'}
          <IconButton size="small" onClick={() => setDialogOpen(false)} sx={{ color: isDark ? '#94A3B8' : '#64748B' }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ px: 2.5, py: 1.5, overflowY: 'auto' }}>
          <Grid container spacing={1.5} sx={{ mt: 0.25 }}>
            <Grid item xs={12}>
              <TextField fullWidth required label="Department ID" value={form.departmentId} size="small"
                onChange={e => setForm(f => ({ ...f, departmentId: e.target.value }))} disabled={!!editing} />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth required label="Name" value={form.name} size="small"
                onChange={e => setForm(f => ({ ...f, name: e.target.value }))} />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth label="English Name" value={form.nameEn} size="small"
                onChange={e => setForm(f => ({ ...f, nameEn: e.target.value }))} />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions
          sx={{
            px: 2.5,
            py: 1.25,
            borderTop: 1,
            borderColor: isDark ? 'rgba(255,255,255,0.08)' : 'divider',
            display: 'flex',
            justifyContent: 'flex-end',
            gap: 1
          }}
        >
          <Button onClick={() => setDialogOpen(false)} sx={{ color: isDark ? '#94A3B8' : '#64748B' }}>Cancel</Button>
          <Button variant="contained" onClick={handleSave} disabled={saving}
            startIcon={saving ? <CircularProgress size={16} /> : <SaveIcon />}>
            {saving ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar(s => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
        <Alert severity={snackbar.severity} variant="filled">{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
}
